import { cartItemFrom, type CartItem } from '@/context/CartContext'
import { FREE_TEXT_SPEC_KEYS, activeGroups, groupList, missingRequired, offeredValues, requiredKeys, specEntries, withoutSkipped } from '@/lib/options'
import { syncPersonalizations } from '@/lib/personalization'
import { rushBlocker } from '@/lib/urgent'
import { itemFontUploads, type Artwork } from '@/lib/artwork'
import type { ItemSpec, OptionGroup, Order, OrderItem, PersonalizationRow, StorefrontProduct } from '@/types'

/**
 * What the live catalogue says about one product an old order names.
 *
 * There is deliberately no 'error' member. A transient failure aborts the whole
 * rebuild before this module runs, because adding only the lines that did
 * resolve would make "Try again" duplicate them. A 404 is a permanent fact -
 * StorefrontController::product aborts 404 on an inactive product, and a
 * product row can never be deleted (no DELETE route, and order_items.product_id
 * is a restricting foreign key), so unavailable always means de-listed.
 */
export type ProductLookup = { status: 'ok'; product: StorefrontProduct } | { status: 'unavailable' }

export type ReorderIssueCode =
  | 'product_unavailable'
  | 'no_price'
  | 'size_removed'
  | 'option_unavailable'
  | 'option_group_hidden'
  | 'option_required'
  | 'personalization_off'
  | 'personalization_resized'
  | 'artwork_missing'
  | 'urgent_unavailable'

/** 'dropped': the line is not in the cart. 'changed': it is, minus something. */
export type ReorderIssueSeverity = 'dropped' | 'changed'

export interface ReorderIssue {
  code: ReorderIssueCode
  severity: ReorderIssueSeverity
  /** The order line this came from, so the panel can group issues by item. */
  itemId: number
  /** The name the OLD order recorded - the live product may not be fetchable. */
  product: string
  /** Set while the product is still live, so the panel can link /p/{id}. */
  productId: number | null
  /** One plain sentence, ready to render. */
  message: string
}

export interface ReorderResult {
  /** Ready for addMany(), in the order's own line order. */
  lines: Omit<CartItem, 'uid'>[]
  issues: ReorderIssue[]
  /** How many of the order's lines produced nothing. */
  droppedCount: number
}

/** Unique product ids the order's lines need, first seen first. One fetch each. */
export function reorderProductIds(order: Order): number[] {
  const seen = new Set<number>()
  for (const item of order.items ?? []) {
    if (typeof item.product_id === 'number') seen.add(item.product_id)
  }
  return [...seen]
}

/**
 * Rebuild an old order as cart lines against today's catalogue.
 *
 * `groups` is NOT optional and must not be undefined: activeGroups(undefined)
 * returns [], which would read every spec key as a hidden group and silently
 * strip every option from every line. The caller loads the groups first.
 *
 * Every rule below mirrors one in OrderService::place, because a line this
 * accepts is one the server has to accept too - the alternative is the
 * customer eating a 422 on the last screen.
 */
export function rebuildOrder(
  order: Order,
  products: Map<number, ProductLookup>,
  groups: OptionGroup[],
): ReorderResult {
  const activeBySpecKey = new Map(activeGroups(groups).map((group) => [group.spec_key, group]))
  const lines: Omit<CartItem, 'uid'>[] = []
  const issues: ReorderIssue[] = []
  let droppedCount = 0

  for (const item of order.items ?? []) {
    const name = item.product || 'This item'
    const lookup = typeof item.product_id === 'number' ? products.get(item.product_id) : undefined

    const note = (
      code: ReorderIssueCode,
      severity: ReorderIssueSeverity,
      message: string,
      productId: number | null = item.product_id ?? null,
    ) => issues.push({ code, severity, itemId: item.id, product: name, productId, message })

    const drop = (code: ReorderIssueCode, message: string, productId: number | null = item.product_id ?? null) => {
      droppedCount += 1
      note(code, 'dropped', message, productId)
    }

    // 1. The product itself.
    if (!lookup || lookup.status === 'unavailable') {
      drop('product_unavailable', `${name} is no longer available, so it was not added.`, null)
      continue
    }
    const product = lookup.product

    // 2. Sizes. Nothing validates these on the server, so this is the only
    //    thing stopping a size the factory stopped making reaching a job sheet.
    const sizeMatrix: Record<string, number> = {}
    const lostSizes: string[] = []
    let lostPieces = 0
    for (const [size, qty] of Object.entries(item.size_matrix ?? {})) {
      if (!(qty > 0)) continue
      if (product.sizes.includes(size)) sizeMatrix[size] = qty
      else {
        lostSizes.push(size)
        lostPieces += qty
      }
    }
    if (lostSizes.length > 0) {
      const sizeList = lostSizes.join(', ')
      const plural = lostPieces === 1 ? 'piece' : 'pieces'
      if (Object.keys(sizeMatrix).length === 0) {
        drop('size_removed', `${name} is no longer made in ${sizeList}, so it was not added.`)
        continue
      }
      note('size_removed', 'changed', `${name} is no longer made in ${sizeList}, so ${lostPieces} ${plural} were not added.`)
    } else if (Object.keys(sizeMatrix).length === 0) {
      drop('size_removed', `${name} had no pieces left to add.`)
      continue
    }

    // 3. Price. Mirrors the server refusing unit_price <= 0, and a line priced
    //    at RM 0.00 in the cart is worse than saying so here. A price agreed
    //    with this customer is a price, whatever the tier table holds.
    if (product.special_price == null && (product.tier_prices ?? []).length === 0) {
      drop('no_price', `${name} has no price set right now. Please contact MPG Printing.`)
      continue
    }

    // 4. Options. specEntries skips empties, walks groups in admin order and
    //    hands back each group's label for the message.
    const spec: ItemSpec = {}
    let optionGone = false
    for (const entry of specEntries(item.spec, groups)) {
      if (FREE_TEXT_SPEC_KEYS.includes(entry.key)) {
        spec[entry.key] = entry.value
        continue
      }

      const group = activeBySpecKey.get(entry.key)
      if (!group) {
        // The server drops a hidden group's key from validated(), so carrying
        // it would show an option on the cart line that the placed order will
        // not have. Dropping it here is what keeps the cart honest.
        note('option_group_hidden', 'changed', `${entry.label} is no longer offered, so the re-ordered ${name} does not carry it.`)
        continue
      }

      const offered = offeredValues(product.options, group)
      // A product that lists nothing in a group accepts anything, exactly as
      // validateSpec does. Emptiness is tested BEFORE membership, term for
      // term with the server - the other way round drops valid lines.
      if (offered.length === 0 || offered.includes(entry.value)) {
        spec[entry.key] = entry.value
        continue
      }

      drop('option_unavailable', `${entry.label} ${entry.value} is no longer offered on ${name}. Configure it again to order it.`)
      optionGone = true
      break
    }
    if (optionGone) continue

    // 4a. A choice that now skips another group (Muslimah skips Sleeve type)
    //     leaves that group off the line, as the server would at placement.
    const kept = withoutSkipped(spec, groups)
    for (const key of Object.keys(spec)) {
      if (!(key in kept)) delete spec[key]
    }

    // 4b. Compulsory options. An older order may have skipped a group this
    //     product now insists on; the server would refuse that line at
    //     checkout, so it is not added - the rule of OrderService::validateSpec.
    const missing = missingRequired(spec, requiredKeys(product.options), groups)
    if (missing.length > 0) {
      drop('option_required', `${name} needs a ${groupList(missing)} chosen now. Configure it again to order it.`)
      continue
    }

    // 5. Name and number.
    let personalizations: PersonalizationRow[] = []
    const rows = item.personalizations ?? []
    if (rows.length > 0) {
      if (product.options?.allow_personalization !== true) {
        // Kept rather than dropped, unlike a missing option: no configuration
        // gets personalization back, because the product no longer sells it.
        note('personalization_off', 'changed', `Name and number is no longer offered on ${name}, so the re-ordered item has none.`)
      } else {
        personalizations = syncPersonalizations(rows, sizeMatrix, product.sizes)
        const lost = rows.length - personalizations.length
        if (lost > 0) {
          note('personalization_resized', 'changed', `${lost} name row(s) on ${name} went with the sizes that are no longer made.`)
        }
      }
    }

    // 6. Artwork. Only the key reaches the server, but the whole reference is
    //    what checkout seeds ArtworkPicker from, so the file shows as attached
    //    with nothing to upload.
    const artworks = artworksFrom(item)
    const artworkName = item.artwork_name ?? null
    if (artworkName && artworks.length === 0) {
      note('artwork_missing', 'changed', `The print file for ${name} is not on the old order, so you will be asked for it at checkout.`)
    }

    // 7. Rush, informational only - canRush() already refuses it at checkout.
    //    This line alone past the cap means the product total is too.
    if (order.is_urgent) {
      const blocker = rushBlocker(name, product.urgent_tiers, Object.values(sizeMatrix).reduce((a, b) => a + b, 0))
      if (blocker) note('urgent_unavailable', 'changed', `${blocker}, so it can no longer be rushed as before.`)
    }

    // cartItemFrom is the one place that knows which snapshot fields a line
    // carries, and it reads every one off the FRESH product. Never hand-build.
    // 6b. The font behind the names travels with them, keys only like artwork.
    const fonts = personalizations.length > 0 ? itemFontUploads(item) : []

    lines.push(cartItemFrom(product, { spec, sizeMatrix, personalizations, artworkName, artworks, fonts }))
  }

  return { lines, issues, droppedCount }
}

/**
 * Every file on the old line that can be re-sent, in order. Only files with a
 * key: a name recorded before uploads existed has nothing behind it. An order
 * payload from before several files existed has no `artworks`, so its one
 * artwork_* file stands in.
 */
function artworksFrom(item: OrderItem): Artwork[] {
  const files = item.artworks ?? [
    { name: item.artwork_name ?? null, path: item.artwork_path ?? null, key: item.artwork_key ?? null, size: item.artwork_size ?? null, mime: item.artwork_mime ?? null },
  ]

  return files
    .filter((file) => file.key)
    .map((file) => ({
      artwork_name: file.name ?? 'artwork',
      artwork_path: file.path ?? '',
      artwork_key: file.key!,
      artwork_size: file.size ?? 0,
      artwork_mime: file.mime ?? '',
    }))
}
